import PropTypes from 'prop-types';
import React from 'react';
import { M } from '@dashboard-experience/mastodon';
import T from 'components/T';
import { i18n } from '@international/mastodon-i18n';
import Status from '../Status';
import { toStartCase } from '../lib/formatting';
import formattedInputValue, {
  formattedSubResultInputValue,
  getEffectivePartnerResults,
  getUniqueSubResults,
  resultKey,
} from './ProLicenseVerificationHelpers';
import {
  ReportTable,
  ReportTableBody,
  ReportTableCell,
  ReportTableColumnHeader,
  ReportTableHead,
  ReportTableRow,
} from 'components/Reports/Report/MastodonReport/ReportTable';
import { useMastodonReport } from 'components/Reports/Report/MastodonReport/MastodonReportContext';

const CONSIDER = 'consider';

// With the flag on the title row is the table head, so its titles are column
// headers; with it off it stays the first body row.
const ResultsTableRows = ({ titleRow, children }) =>
  useMastodonReport() ? (
    <>
      <ReportTableHead>{titleRow}</ReportTableHead>
      <ReportTableBody>{children}</ReportTableBody>
    </>
  ) : (
    <ReportTableBody>
      {titleRow}
      {children}
    </ReportTableBody>
  );

ResultsTableRows.propTypes = {
  titleRow: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
};

class ProLicenseVerificationCertificationResults extends React.Component {
  formatConsiderSubChecks(result) {
    const subChecks = (result.subChecks || [])
      .filter(subCheck => subCheck.status.toLowerCase() === CONSIDER)
      .map(
        subCheck =>
          i18n.getStr(
            `components.Report.ProLicenseVerification.subChecks.${subCheck.name}`,
          ) || '',
      )
      .join(',');
    return subChecks ? `(${subChecks})` : '';
  }

  renderCertificationResult(certificationsEntry) {
    const { input } = certificationsEntry;
    const partnerResults = getEffectivePartnerResults(certificationsEntry);
    const primaryResult = partnerResults[0] || certificationsEntry.result || {};
    return (
      <div className='mb-4' key={`div-${primaryResult.certificationIssuer}`}>
        {this.renderCertificationResultHeader(primaryResult)}
        {this.renderCertificationResultLicenseStatusExpiration(primaryResult)}
        <br />
        <ReportTable small>
          <ResultsTableRows
            titleRow={
              <ReportTableRow>
                <ReportTableCell />
                <ReportTableColumnHeader>
                  <b>
                    <T value='components.Report.ProLicenseVerification.candidateInput' />
                  </b>
                </ReportTableColumnHeader>
                {partnerResults.map((r, idx) => (
                  <ReportTableColumnHeader key={`header-${resultKey(r, idx)}`}>
                    <b>
                      <T value='components.Report.ProLicenseVerification.matchedResult' />
                    </b>
                  </ReportTableColumnHeader>
                ))}
              </ReportTableRow>
            }
          >
            {this.renderCertificationResultOrderDetail(partnerResults, input)}
            {this.renderCertificationSubResults(partnerResults, input)}
          </ResultsTableRows>
        </ReportTable>
      </div>
    );
  }

  renderCertificationResultHeader(result) {
    return (
      <div>
        <div className='d-flex'>
          <div>
            <b>{result.certificationIssuer}</b>
          </div>
          <div className='ml-auto'>
            <Status status={result.active ? 'clear' : 'consider'} />
          </div>
        </div>
        <hr />
      </div>
    );
  }

  renderCertificationResultLicenseStatusExpiration(result) {
    const icon = result.active ? 'Checkmark' : 'WarningFilled';

    return (
      <M.Grid>
        <M.GridRow className='mb-1'>
          <M.GridCol sm={4} md={2}>
            <T value='components.Report.ProLicenseVerification.licenseStatus' />
            :
          </M.GridCol>
          <M.GridCol sm={8} md={10}>
            <span
              className='d-inline-flex align-items-center'
              style={{ gap: '4px' }}
            >
              <M.Icon icon={icon} />
              <b>
                <T
                  value={`components.Report.ProLicenseVerification.${
                    result.active ? 'verified' : 'unverified'
                  }`}
                />
              </b>
              {` ${this.formatConsiderSubChecks(result)}`}
            </span>
          </M.GridCol>
        </M.GridRow>
        <M.GridRow className='mb-1'>
          <M.GridCol sm={4} md={2}>
            <T value='components.Report.ProLicenseVerification.expirationDate' />
            :
          </M.GridCol>
          <M.GridCol sm={8} md={10}>
            {result.certificationExpiration || '-'}
          </M.GridCol>
        </M.GridRow>
      </M.Grid>
    );
  }

  renderCertificationResultOrderDetail(partnerResults, input) {
    const { candidateName } = this.props;

    const certificationOrderDetails = [
      'certificationIssuedTo',
      'certificationIssuerRegion',
      'certificationIssuer',
      'certificationName',
    ];

    return certificationOrderDetails.map(key => {
      return (
        <ReportTableRow key={key}>
          <ReportTableCell>
            <span className='text-muted'>
              <T
                value={`components.Report.ProLicenseVerification.orderDetails.${key}`}
              />
            </span>
          </ReportTableCell>
          <ReportTableCell>
            <span>{formattedInputValue(key, input, candidateName)}</span>
          </ReportTableCell>
          {partnerResults.map((r, idx) => (
            <ReportTableCell key={`${key}-${resultKey(r, idx)}`}>
              <span>{r[key] || '-'}</span>
            </ReportTableCell>
          ))}
        </ReportTableRow>
      );
    });
  }

  renderCertificationSubResults(partnerResults, input) {
    const subResults = getUniqueSubResults(partnerResults);

    return subResults.map(subResult => {
      return (
        <ReportTableRow key={subResult.name}>
          <ReportTableCell>
            <span className='text-muted'>{toStartCase(subResult.name)}</span>
          </ReportTableCell>
          <ReportTableCell>
            <span>{formattedSubResultInputValue(subResult, input)}</span>
          </ReportTableCell>
          {partnerResults.map((r, idx) => {
            const matching = (r.subResults || []).find(
              sr => sr.name === subResult.name,
            );
            return (
              <ReportTableCell
                key={`sub-${subResult.name}-${resultKey(r, idx)}`}
              >
                <span>{matching?.value || '-'}</span>
              </ReportTableCell>
            );
          })}
        </ReportTableRow>
      );
    });
  }

  render() {
    const { certifications } = this.props;

    return certifications.map(certificationsEntry => {
      return this.renderCertificationResult(certificationsEntry);
    });
  }
}

ProLicenseVerificationCertificationResults.propTypes = {
  certifications: PropTypes.array,
  candidateName: PropTypes.string,
};

ProLicenseVerificationCertificationResults.defaultProps = {
  certifications: [],
  candidateName: '',
};

export default i18n.renderTranslation()(
  ProLicenseVerificationCertificationResults,
);
